import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Group,
  Text,
  Button,
  Paper,
  TextInput,
  Select,
  LoadingOverlay,
  Badge,
  Card,
  Image,
  Stack,
  SimpleGrid,
  Center,
  ThemeIcon,
  Pagination,
  ActionIcon, // NEW
  NumberInput, // NEW
  Divider, // NEW
  Tooltip, // NEW
} from "@mantine/core";
import {
  IconSearch,
  IconPlus,
  IconPackage,
  IconFilter,
  IconChevronRight,
  IconCalendarEvent,
  IconStar, // NEW
  IconStarFilled, // NEW
} from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { AdminTicketManagerModal } from "./AdminTicketManagerModal/AdminTicketManagerModal";
import { API_BASE_URL } from "../../config/baseUrls";

// --- API HELPERS ---
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

export default function ProductManagement() {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification(); // Added showSuccess

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
  });

  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [filters, setFilters] = useState({
    status: "",
    type: "",
    search: "",
    sortBy: "newest",
  });

  const openTicketManager = (product) => {
    setSelectedEvent(product);
    setTicketModalOpen(true);
  };

  // --- FETCH DATA ---
  const fetchData = async (pageToFetch = pagination.page) => {
    setLoading(true);
    try {
      const params = {
        page: pageToFetch,
        limit: pagination.limit,
        sortBy: filters.sortBy,
        search: filters.search,
        status: filters.status === "ALL" ? "" : filters.status,
        productType: filters.type === "ALL" ? "" : filters.type,
      };

      const query = new URLSearchParams(params);
      const response = await fetch(
        `${API_BASE_URL}/admin/products?${query}`,
        { headers: getAuthHeaders() },
      );
      const data = await response.json();

      if (data.success) {
        setProducts(data.data.products);
        setPagination(data.data.pagination);
      }
    } catch (err) {
      showError("Error", "Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, [filters]);

  // --- NEW: QUICK UPDATE HANDLER ---
  const handleQuickUpdate = async (id, field, value) => {
    // 1. Optimistic Update (Update UI immediately)
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)),
    );

    try {
      // 2. API Call in Background
      const res = await fetch(
        `${API_BASE_URL}/admin/products/${id}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({ [field]: value }),
        },
      );

      if (!res.ok) throw new Error("Update failed");
    } catch (error) {
      showError("Error", "Failed to update product");
      fetchData(); // Revert changes on error
    }
  };

  // --- UI HELPERS ---
  const StatusBadge = ({ status }) => {
    const map = {
      AVAILABLE: { color: "green", label: "Active" },
      OUT_OF_STOCK: { color: "red", label: "Out of Stock" },
      SUSPENDED: { color: "yellow", label: "Suspended" },
      TESTING: { color: "blue", label: "Testing" },
      DISCONTINUED: { color: "gray", label: "Archived" },
    };
    const conf = map[status] || { color: "gray", label: status };
    return (
      <Badge color={conf.color} variant="light" size="sm">
        {conf.label}
      </Badge>
    );
  };

  return (
    <Box className="space-y-6">
      {/* HEADER */}
      <Group justify="space-between" align="end">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            Store Management
          </Text>
          <Text size="sm" c="dimmed">
            Manage your physical inventory and live events
          </Text>
        </Box>
        <Button
          leftSection={<IconPlus size={18} />}
          className="btn-ota-primary"
          onClick={() => navigate("/admin/dashboard/product-management/create")}
        >
          Add Item
        </Button>
      </Group>

      {/* FILTERS BAR */}
      <Paper p="sm" radius="md" withBorder>
        <Group grow>
          <TextInput
            placeholder="Search by title..."
            leftSection={<IconSearch size={16} />}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <Select
            placeholder="Item Type"
            data={[
              { value: "ALL", label: "All Types" },
              { value: "SHOP_ITEM", label: "Physical Products" },
              { value: "EVENT", label: "Events" },
              { value: "MOVIE_SCREENING", label: "Movies" },
            ]}
            value={filters.type || "ALL"}
            onChange={(v) => setFilters({ ...filters, type: v })}
          />
          <Select
            placeholder="Status"
            data={["ALL", "AVAILABLE", "OUT_OF_STOCK", "SUSPENDED", "TESTING"]}
            value={filters.status || "ALL"}
            onChange={(v) => setFilters({ ...filters, status: v })}
          />
          <Select
            placeholder="Sort By"
            data={[
              { value: "newest", label: "Newest Added" },
              { value: "price_high", label: "Price: High to Low" },
              { value: "price_low", label: "Price: Low to High" },
            ]}
            value={filters.sortBy}
            onChange={(v) => setFilters({ ...filters, sortBy: v })}
          />
          <Button
            variant="light"
            color="gray"
            onClick={() => fetchData(pagination.page)}
            leftSection={<IconFilter size={16} />}
            w={120}
            style={{ flexGrow: 0 }}
          >
            Refresh
          </Button>
        </Group>
      </Paper>

      {/* LIST CONTENT */}
      <Box pos="relative" mih={300}>
        <LoadingOverlay
          visible={loading}
          overlayProps={{ radius: "sm", blur: 2 }}
        />

        {products.length === 0 && !loading ? (
          <Paper p="xl" withBorder radius="md" ta="center" bg="gray.0">
            <ThemeIcon
              size={60}
              radius="xl"
              color="gray"
              variant="light"
              mb="md"
            >
              <IconPackage size={30} />
            </ThemeIcon>
            <Text fw={600} size="lg">
              No items found
            </Text>
            <Text c="dimmed">Adjust filters or add a new item.</Text>
          </Paper>
        ) : (
          <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">
            {products.map((item) => {
              const isEvent = ["EVENT", "MOVIE_SCREENING"].includes(
                item.productType,
              );

              return (
                <Card
                  key={item.id}
                  padding="md"
                  radius="md"
                  withBorder
                  className="hover:shadow-md transition-all cursor-pointer group"
                  onClick={() =>
                    navigate(
                      `/admin/dashboard/product-management/manage/${item.id}`,
                    )
                  }
                >
                  <Group wrap="nowrap" align="start">
                    {/* Image */}
                    <Box
                      w={80}
                      h={80}
                      bg="gray.1"
                      style={{
                        borderRadius: 8,
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      {item.primaryImage ? (
                        <Image
                          src={item.primaryImage}
                          w="100%"
                          h="100%"
                          fit="cover"
                        />
                      ) : (
                        <Center h="100%">
                          {isEvent ? (
                            <IconCalendarEvent color="gray" />
                          ) : (
                            <IconPackage color="gray" />
                          )}
                        </Center>
                      )}
                    </Box>

                    {/* Info */}
                    <Stack gap={4} style={{ flex: 1 }}>
                      <Text fw={600} lineClamp={1} title={item.title}>
                        {item.title}
                      </Text>
                      <Group gap="xs">
                        <StatusBadge status={item.status} />
                        <Text size="xs" c="dimmed" tt="capitalize">
                          {item.productType?.replace("_", " ").toLowerCase()}
                        </Text>
                      </Group>

                      <Group justify="space-between" mt="xs">
                        <Text fw={700} size="sm">
                          ₹{item.unitPrice?.toLocaleString()}
                        </Text>
                        <Text
                          size="xs"
                          c={item.availableQuantity < 5 ? "red" : "dimmed"}
                        >
                          {isEvent ? "Capacity: " : "Stock: "}
                          <b>{item.availableQuantity}</b>
                        </Text>
                      </Group>
                    </Stack>
                  </Group>

                  {/* NEW: FEATURED MANAGEMENT SECTION */}
                  <Divider my="sm" />
                  <Group justify="space-between" align="center">
                    <Group gap={8}>
                      <Tooltip
                        label={
                          item.isFeatured ? "Unfeature" : "Feature this item"
                        }
                      >
                        <ActionIcon
                          variant={item.isFeatured ? "light" : "subtle"}
                          color={item.isFeatured ? "yellow" : "gray"}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickUpdate(
                              item.id,
                              "isFeatured",
                              !item.isFeatured,
                            );
                          }}
                        >
                          {item.isFeatured ? (
                            <IconStarFilled size={18} />
                          ) : (
                            <IconStar size={18} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                      <Text size="xs" c="dimmed" fw={500}>
                        {item.isFeatured ? "Featured" : "Standard"}
                      </Text>
                    </Group>

                    {/* Show Priority Input ONLY if Featured */}
                    {item.isFeatured && (
                      <NumberInput
                        size="xs"
                        w={70}
                        min={0}
                        placeholder="Ord"
                        value={item.priorityOrder}
                        onChange={(val) =>
                          handleQuickUpdate(item.id, "priorityOrder", val)
                        }
                        onClick={(e) => e.stopPropagation()}
                        rightSectionWidth={20}
                        styles={{ input: { textAlign: "center" } }}
                      />
                    )}
                  </Group>
                </Card>
              );
            })}
          </SimpleGrid>
        )}

        {/* PAGINATION */}
        {pagination.totalPages > 1 && (
          <Center mt="xl">
            <Pagination
              total={pagination.totalPages}
              value={pagination.page}
              onChange={(p) => {
                setPagination({ ...pagination, page: p });
                fetchData(p);
              }}
            />
          </Center>
        )}
      </Box>
    </Box>
  );
}
